import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { NotFound } from '../components/layout'
import { useBoardState, useStore } from '../store/context'
import { SETTINGS_ENTRIES, settingsMatch, type SettingsEntry } from './registry'
import { movedSettingsPath, readSettingsPath, settingsPath, SETTINGS_OWNERS } from './routing'
import { SettingsProject } from './selection'
import { SettingsColumn, type ColumnGroup } from './SettingsColumn'
import { settingsWords as words } from './words'
import { AccountSettings } from './AccountSettings'
import { ProjectsSettings } from './ProjectsSettings'
import { RepositorySettings } from './RepositorySettings'
import { ColumnSettings } from './ColumnSettings'
import { LabelSettings } from './LabelSettings'
import { FlowSettings } from './FlowSettings'
import { AdvancedSettings } from './AdvancedSettings'
import { ProgramTokensSettings, TeachSettings } from './ExternalAgents'
import { AgentSettings } from './AgentSettings'
import { RunnersPage } from '../runners/RunnersPage'
import { canInteract, useTabScope } from '../workspace/context'

const CONTROLS = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, [tabindex="0"]'

/**
 * Settings (SUN-1454): a sheet over the app, with an address. Its column
 * lists every section and is the one way to move; the pane beside it shows
 * the section the address names. History returns through the sections and
 * closes the sheet. The page under it keeps its title, so no tab of the Mac
 * app is ever called Settings.
 */
export function SettingsPage({ onClose }: { onClose?: () => void }) {
  const scope = useTabScope()
  const { projects: projectMap, selectedProjectId, me } = useBoardState()
  const store = useStore()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const sheet = useRef<HTMLDivElement>(null)
  const pane = useRef<HTMLElement>(null)
  const opener = useRef(document.activeElement as HTMLElement | null)
  const heading = useId()
  const [query, setQuery] = useState('')
  // A narrow window shows the column or the pane, one at a time.
  const [level, setLevel] = useState<'pane' | 'column'>('pane')
  // The section just chosen in the column, until its pane has the focus.
  const chosen = useRef<string | null>(null)
  const [choices, setChoices] = useState(0)
  const close = () => onClose ? onClose() : navigate('/')

  useEffect(() => {
    const previous = opener.current
    if (canInteract(scope)) sheet.current?.focus()
    return () => {
      if (!canInteract(scope)) return
      if (previous?.isConnected && previous !== document.body) previous.focus()
      else (scope ? document.querySelector(`[data-workspace-chrome-tab="${scope.id}"]`) : document)?.querySelector<HTMLElement>('.person-button')?.focus()
    }
  }, [])
  // The pane takes the focus, not its heading: a screen reader says the section's name and no box is drawn.
  useLayoutEffect(() => {
    if (chosen.current !== pathname) return
    chosen.current = null
    pane.current?.focus()
  }, [pathname, choices])
  useEffect(() => {
    if (level === 'column') sheet.current?.querySelector<HTMLElement>('.settings-row[tabindex="0"]')?.focus()
  }, [level])
  const choose = (to: string) => { chosen.current = to; setChoices(n => n + 1); setQuery(''); setLevel('pane') }

  const projects = [...projectMap.values()]
  const entries = SETTINGS_ENTRIES.filter(entry => !['runners', 'agents', 'programs', 'tools'].includes(entry.id) || (!store.byToken && me?.member))
  const at = readSettingsPath(pathname)
  const moved = movedSettingsPath(pathname, (projects.find(project => project.id === selectedProjectId) ?? projects[0])?.id)
  const project = at.owner === 'project' ? projects.find(project => project.id === at.projectId) : undefined
  const entry = entries.find(entry => entry.owner === at.owner && entry.id === at.section && (entry.owner !== 'project' || project))
  const row = (entry: SettingsEntry, id?: string) => ({
    id: entry.id,
    to: settingsPath(entry.owner, entry.id, id),
    current: !moved && at.owner === entry.owner && at.section === entry.id && at.projectId === id,
  })
  const groups: ColumnGroup[] = [
    ...SETTINGS_OWNERS.flatMap(owner => {
      const things = owner === 'project' ? projects.map(project => ({ key: `project/${project.id}`, label: project.name, project: project.id }))
        : [{ key: owner, label: words.groups[owner], project: undefined }]
      return things.map(thing => ({
        key: thing.key,
        label: thing.label,
        rows: entries.filter(entry => entry.owner === owner && settingsMatch(entry, query, thing.label)).map(entry => row(entry, thing.project)),
      }))
    }),
  ].filter(group => group.rows.length > 0)

  function keys(event: KeyboardEvent<HTMLDivElement>) {
    if (event.defaultPrevented) return
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      // In the field, with something typed, Escape empties it first, as a search field does.
      if (query && (event.target as HTMLElement).closest('.settings-find')) setQuery('')
      else close()
      return
    }
    if (event.key !== 'Tab') return
    const items = [...event.currentTarget.querySelectorAll<HTMLElement>(CONTROLS)]
      .filter(item => item.tabIndex >= 0 && !item.closest('[inert]') && !item.hidden && [item, ...ancestors(item, event.currentTarget)].every(each => getComputedStyle(each).display !== 'none'))
    const first = items[0], last = items.at(-1)
    if (event.shiftKey && (document.activeElement === first || document.activeElement === event.currentTarget)) { event.preventDefault(); last?.focus() }
    else if (!event.shiftKey && (document.activeElement === last || document.activeElement === event.currentTarget)) { event.preventDefault(); first?.focus() }
  }

  const group = project ? project.name : entry && entry.owner !== 'project' ? words.groups[entry.owner] : null
  const sections = { programs: ProgramTokensSettings, tools: TeachSettings, account: AccountSettings, projects: ProjectsSettings, runners: RunnersPage, agents: AgentSettings, repository: RepositorySettings, labels: LabelSettings, columns: ColumnSettings, flow: FlowSettings, advanced: AdvancedSettings }
  const Section = entry ? sections[entry.id] : null
  return <div className="settings-veil">
    <div ref={sheet} className="settings-sheet" role="dialog" aria-modal="true" aria-label="Settings" tabIndex={-1} data-level={level} onKeyDown={keys}>
      {moved && <Navigate to={moved} replace />}
      <SettingsColumn groups={groups} query={query} onQuery={setQuery} onChoose={choose} onClose={close} />
      <section ref={pane} className="settings-pane" tabIndex={-1} aria-labelledby={entry ? heading : undefined} aria-label={entry ? undefined : 'Settings'}>
        <header className="settings-pane-head">
          <button type="button" className="button settings-back" onClick={() => setLevel('column')}>‹ {words.back}</button>
          <div className="settings-pane-what">
            {entry && <>
              {group && <p className="settings-pane-group">{group}</p>}
              <h1 id={heading}>{words.entries[entry.id].label}</h1>
            </>}
          </div>
          <button type="button" className="button" onClick={close}>{words.done}</button>
        </header>
        <div className="settings-pane-body" key={pathname}>
          {moved ? null : !Section ? <NotFound what="page" /> : <>
            <SettingsProject.Provider value={project?.id ?? null}>
              <div className="settings-section-page"><Section /></div>
            </SettingsProject.Provider>
          </>}
        </div>
      </section>
    </div>
  </div>
}

function* ancestors(element: HTMLElement, root: HTMLElement): Generator<HTMLElement> {
  let parent = element.parentElement
  while (parent && parent !== root) { yield parent; parent = parent.parentElement }
}
